import { cva, type VariantProps } from 'class-variance-authority'
import { memo, type ReactNode } from 'react'

import { cn } from '@/lib/utils'

import type { InvoiceStatus } from './billing.interface'

const billingInvoiceStatusBadgeVariants = cva(
  'billing-invoice-status-badge inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium',
  {
    variants: {
      status: {
        draft: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
        open: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300',
        paid: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
        void: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
        uncollectible: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
      },
    },
    defaultVariants: {
      status: 'draft',
    },
  }
)

export interface BillingInvoiceStatusBadgeProps
  extends VariantProps<typeof billingInvoiceStatusBadgeVariants> {
  status: InvoiceStatus
  className?: string
}

const statusLabelMap: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  open: 'Open',
  paid: 'Paid',
  void: 'Void',
  uncollectible: 'Uncollectible',
}

const BillingInvoiceStatusBadgeBase: React.FC<BillingInvoiceStatusBadgeProps> = ({
  status,
  className,
}): ReactNode => (
  <span className={cn(billingInvoiceStatusBadgeVariants({ status }), className)}>
    {statusLabelMap[status]}
  </span>
)

export const BillingInvoiceStatusBadge = memo(BillingInvoiceStatusBadgeBase)

BillingInvoiceStatusBadge.displayName = 'BillingInvoiceStatusBadge'

export { billingInvoiceStatusBadgeVariants }
